import React from 'react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { X } from 'lucide-react';

interface SettingsModalProps {
  isOpen: boolean;
  autoShowImage: boolean;
  onAutoShowImageChange: (value: boolean) => void;
  enableDragAndDrop: boolean;
  onEnableDragAndDropChange: (value: boolean) => void;
  enableRapidScanning: boolean;
  onEnableRapidScanningChange: (value: boolean) => void;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  autoShowImage,
  onAutoShowImageChange,
  enableDragAndDrop,
  onEnableDragAndDropChange,
  enableRapidScanning,
  onEnableRapidScanningChange,
  onClose
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-md overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">{t('common.buttons.settings')}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>
        <div className="p-4 sm:p-6">
          <div className="space-y-6 sm:space-y-4">
            {/* Auto-show image toggle */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  {t('warehouse.modules.picking.pickingSettings.autoShowImage')}
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  {t('warehouse.modules.picking.pickingSettings.autoShowImageDescription')}
                </p>
              </div>
              <button
                onClick={() => onAutoShowImageChange(!autoShowImage)}
                className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] ${
                  autoShowImage ? 'bg-blue-600' : 'bg-gray-200'
                }`}
                role="switch"
                aria-checked={autoShowImage}
              >
                <span
                  className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    autoShowImage ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Drag and drop toggle */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  {t('warehouse.modules.picking.pickingSettings.lineSorting')}
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  {t('warehouse.modules.picking.pickingSettings.lineSortingDescription')}
                </p>
              </div>
              <button
                onClick={() => onEnableDragAndDropChange(!enableDragAndDrop)}
                className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] ${
                  enableDragAndDrop ? 'bg-blue-600' : 'bg-gray-200'
                }`}
                role="switch"
                aria-checked={enableDragAndDrop}
              >
                <span
                  className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    enableDragAndDrop ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Rapid scanning toggle */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  {t('warehouse.modules.picking.pickingSettings.rapidScanning')}
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  {t('warehouse.modules.picking.pickingSettings.rapidScanningDescription')}
                </p>
              </div>
              <button
                onClick={() => onEnableRapidScanningChange(!enableRapidScanning)}
                className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] ${
                  enableRapidScanning ? 'bg-blue-600' : 'bg-gray-200'
                }`}
                role="switch"
                aria-checked={enableRapidScanning}
              >
                <span
                  className={`pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    enableRapidScanning ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6 border-t border-gray-200 flex justify-end sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-6 py-3 sm:py-2 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('common.buttons.close')}
          </button>
        </div>
      </div>
    </div>
  );
};
